import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Eye, EyeOff, Newspaper, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { ConfirmDialog, EmptyState, PageHeader, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";

export default function BlogAdmin() {
  const [items, setItems] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => {
    const q = new URLSearchParams();
    if (statusFilter) q.set("status", statusFilter);
    if (search) q.set("search", search);
    api.get(`/blogs?${q}`).then(({ data }) => setItems(data.items)).catch((e) => toast.error(errMsg(e)));
  }, [statusFilter, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const togglePublish = async (post) => {
    try {
      await api.put(`/blogs/${post.id}`, { ...post, status: post.status === "published" ? "draft" : "published" });
      toast.success(post.status === "published" ? "Post unpublished" : "Post published");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/blogs/${deleting.id}`);
      toast.success("Post deleted");
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="blog-admin-page">
      <PageHeader title="Blog Management" description="Publish insights that bring clients to your door." testId="blog-admin-header">
        <Link to="/dashboard/blog/new">
          <Button data-testid="new-post-btn" className="bg-navy text-ivory hover:bg-navy-800 rounded-none uppercase tracking-wider text-xs font-semibold">
            <Plus className="h-4 w-4 mr-1.5" /> New Post
          </Button>
        </Link>
      </PageHeader>

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input data-testid="blog-admin-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search posts…"
            className="w-full bg-white border border-border focus:border-gold px-3 py-2.5 pl-10 outline-none transition text-sm rounded-none" />
        </div>
        <div className="flex border border-border bg-white">
          {["", "published", "draft"].map((s) => (
            <button key={s || "all"} onClick={() => setStatusFilter(s)} data-testid={`blog-filter-${s || "all"}`}
              className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors ${statusFilter === s ? "bg-navy text-ivory" : "text-muted-foreground hover:text-navy"}`}>
              {s || "All"}
            </button>
          ))}
        </div>
      </div>

      {!items ? (
        <div className="space-y-3" data-testid="blog-admin-skeleton">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-white border border-border animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon={Newspaper} title="No posts found"
          description={search || statusFilter ? "Adjust your filters." : "Write your first insight to attract new clients."}
          testId="blog-admin-empty"
          action={<Link to="/dashboard/blog/new"><Button className="bg-navy text-ivory rounded-none" data-testid="empty-new-post"><Plus className="h-4 w-4 mr-1.5" /> New Post</Button></Link>} />
      ) : (
        <div className="space-y-3" data-testid="blog-admin-list">
          {items.map((post) => (
            <div key={post.id} className="bg-white border border-border p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 hover:border-gold transition-colors" data-testid={`blog-row-${post.id}`}>
              <img src={post.cover_image} alt="" className="w-full sm:w-28 h-24 sm:h-16 object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-serif text-lg font-semibold text-navy truncate">{post.title}</h3>
                  <StatusBadge status={post.status} />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {post.category} · {post.read_time} min read · {post.status === "published" ? `Published ${fmtDate(post.published_at)}` : `Updated ${fmtDate(post.updated_at)}`}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => togglePublish(post)} data-testid={`toggle-publish-${post.id}`}
                  className="p-2 text-muted-foreground hover:text-gold-700 hover:bg-gold-100 transition-colors"
                  aria-label={post.status === "published" ? "Unpublish" : "Publish"} title={post.status === "published" ? "Unpublish" : "Publish"}>
                  {post.status === "published" ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
                <Link to={`/dashboard/blog/${post.id}/edit`} data-testid={`edit-post-${post.id}`}
                  className="p-2 text-muted-foreground hover:text-navy hover:bg-muted transition-colors" aria-label="Edit">
                  <Pencil className="h-4 w-4" />
                </Link>
                <button onClick={() => setDeleting(post)} data-testid={`delete-post-${post.id}`}
                  className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 transition-colors" aria-label="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog open={!!deleting} onOpenChange={() => setDeleting(null)}
        title="Delete this post?"
        description={`"${deleting?.title}" will be permanently removed from the site.`}
        confirmLabel="Delete Post" onConfirm={doDelete} />
    </div>
  );
}
